import { FC, useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import styled from "@emotion/styled";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import { useForm } from "react-hook-form";
import { ModalA } from "../../common/components/ModalA";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { Asterisk } from "../../common/components/Asterisk";
import { TaskTemplateWithIdType } from "../types/TaskTemplateType";
import { parseTaskExcelTemplate } from "../miscellaneous/parseTaskExcelTemplate";
import { downloadTaskExcelTemplate } from "../miscellaneous/downloadTaskExcelTemplate";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: "200px",
});

const formControlStyle = {
  marginTop: "20px",
};

const VALIDATION_ERROR_MESSAGE =
  "Cannot upload task template because some data does not meet the required validation rules. Please make sure the entries follow the provided guidance.";

const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "20px"
};

type UploadTaskTemplateProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  questionsType: any[];
  onUploadSuccess: (questions: TaskTemplateWithIdType[]) => void;
};

export const UploadTaskTemplate: FC<UploadTaskTemplateProps> = ({
  isOpen,
  setIsOpen,
  questionsType,
  onUploadSuccess,
}) => {
  const [textFileName, setTextFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isShowToastDanger, setIsShowToastDanger] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    reset,
  } = useForm();

  const handleCloseModal = () => {
    setTextFileName("");
    setSelectedFile(null);
    setIsOpen(false);
    reset();
  };

  const handleSubmitUpload = async () => {
    if (!selectedFile) return;

    const result = await parseTaskExcelTemplate(selectedFile, questionsType);

    if (result.success) {
      onUploadSuccess(result.data);
      handleCloseModal();
    } else {
      setIsShowToastDanger(true);
    }
  };

  const handleCloseToast = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setIsShowToastDanger(false);
  };

  return (
    <>
    <ModalA
      title="Upload a Template"
      onCloseModal={handleCloseModal}
      isOpen={isOpen}
      width={600}
    >
      <form onSubmit={handleSubmit(handleSubmitUpload)}>
        <FormControl fullWidth sx={formControlStyle}>
          <label className="font-bold">
            Upload File{" "}
            <Tooltip
              title={
                <>
                  <p>The maximum file size allowed is 20MB.</p>
                  <p>Maximum number of rows is 1000.</p>
                  <p>
                    Please use Microsoft Excel Version 2013 or later to enter the
                    data to the Excel Template.
                  </p>
                  <p>Supported format: xls, xlsx.</p>
                </>
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    minInlineSize: "fit-content",
                    backgroundColor: "#fff",
                    color: "#333",
                    border: "1px solid #ccc",
                    fontSize: "13px",
                    lineHeight: 1.4,
                    padding: "8px 12px",
                    "& p": { margin: 0 },
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                    "&::before": { border: "1px solid #ccc" },
                  },
                },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: "18px", color: "#203C77", cursor: "pointer", verticalAlign: "middle" }} />
            </Tooltip>
            {" "}<Asterisk />
          </label>

          <div className="flex justify-between gap-2">
            <TextField
              value={textFileName}
              InputProps={{ readOnly: true }}
              sx={{
                flex: 1,
                input: { height: "5px" },
              }}
            />

            <Button
              component="label"
              variant="contained"
              tabIndex={-1}
              sx={{
                backgroundColor: "#203C77",
                color: "#fff",
                borderRadius: "5px",
                minWidth: "100px",
                textTransform: "capitalize",
                "&:hover": { backgroundColor: "#203C77" },
              }}
            >
              Browse
              <VisuallyHiddenInput
                type="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xls, .xlsx"
                {...register("excelFile", {
                  required: "You must choose an Excel file",
                })}
                onChange={(event) => {
                  if (event.target.files && event.target.files[0]) {
                    setSelectedFile(event.target.files[0]);
                    setTextFileName(event.target.files[0].name);
                    clearErrors("excelFile");
                  }
                }}
              />
            </Button>
          </div>

          <FormHelperText sx={{ color: "red" }}>
            {control.getFieldState("excelFile").error?.message}
          </FormHelperText>

          <div className="text-sm mt-3">
            <p>Please ensure the following before proceeding:</p>
            <ul className="list-disc ml-5 mt-1">
              <li>
                <strong>Question</strong> should not be left blank
              </li>
              <li>
                <strong>Question Type</strong> should be set to <strong>Free Text</strong>,{" "}
                <strong>Single Select List</strong>, <strong>Multi-Select List</strong>,{" "}
                <strong>Check Boxes</strong>, or <strong>Radio Buttons</strong>
              </li>
              <li>
                <strong>Choices</strong> should be separated by a{" "}
                <strong>semicolon (;)</strong>
              </li>
            </ul>
          </div>
          <div className="flex justify-start">
            <Button
              onClick={(event) => {
                event.stopPropagation();
                downloadTaskExcelTemplate();
              }}
              sx={{
                textTransform: "capitalize",
                color: "#203C77",
                padding: 0,
                marginTop: "5px",
              }}
            >
              Download Excel Template
            </Button>
          </div>
        </FormControl>

        <Alert
          severity="warning"
          icon={<WarningAmberIcon sx={{ color: "#ed6c02" }} />}
          sx={{ marginTop: "16px" }}
        >
          Please note that using this function will override the existing Template questions.
        </Alert>

        <FormControl fullWidth sx={btnGroupStyle}>
          <ButtonCancelA
            name="Cancel"
            onHandleClick={handleCloseModal}
          />
          <ButtonSubmitA
            name="Upload"
          />
        </FormControl>
      </form>
    </ModalA>

    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      open={isShowToastDanger}
      autoHideDuration={8000}
      onClose={handleCloseToast}
    >
      <Alert
        onClose={handleCloseToast}
        severity="error"
        variant="filled"
        sx={{ width: "100%" }}
      >
        <p>
          {VALIDATION_ERROR_MESSAGE}
        </p>
      </Alert>
    </Snackbar>
    </>
  );
};
